/*
 * PROTOTYPE — Cyber-Luxe theme preview (Dark + Light).
 *
 * One picked direction (D), two modes. Mirrors the real AdminLayout
 * structure (w-60 sidebar + right-aligned header) so this previews what
 * the actual app will look like, not an isolated mockup.
 *
 *   ?variant=dark | light    — colour mode
 *   ?section=app | login     — surface to preview
 *
 * Delete this file + its route entry once the tokens are folded into
 * the real theme system.
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  UserPlus,
  Users,
  Server,
  Building2,
  Bell,
  Search,
  Globe,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
  Shield,
  Cpu,
  Eye,
  EyeOff,
  Sparkles,
  LogOut,
  Menu,
  Sun,
  Moon,
} from "lucide-react";

const VARIANTS = [
  { key: "dark", label: "Cyber-Luxe · Dark" },
  { key: "light", label: "Cyber-Luxe · Light" },
];

const SECTIONS = [
  { key: "app", label: "App Frame" },
  { key: "login", label: "Login" },
];

/* ─────────────────────────────────────────────────────────────────────────
 * Theme tokens — one shape, two palettes
 * ──────────────────────────────────────────────────────────────────────── */
const TOKENS = {
  dark: {
    name: "dark",
    logo: "/brand/quantum-logo-dark.png",
    pageBg:
      "radial-gradient(1200px 700px at 80% -10%, rgba(124,58,237,0.12), transparent 60%), radial-gradient(900px 500px at -10% 110%, rgba(34,197,94,0.10), transparent 60%), #0B0C0E",
    surfaceBg: "rgba(11,12,14,0.7)",
    sidebarBg: "rgba(11,12,14,0.6)",
    headerBg: "rgba(11,12,14,0.55)",
    border: "rgba(255,255,255,0.06)",
    borderStrong: "rgba(255,255,255,0.08)",
    cardBg:
      "linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.015) 100%)",
    cardTightBg:
      "linear-gradient(180deg, rgba(255,255,255,0.035) 0%, rgba(255,255,255,0.01) 100%)",
    inputBg: "rgba(255,255,255,0.03)",
    inputBorder: "rgba(255,255,255,0.07)",
    textPrimary: "#F4F5F7",
    textSecondary: "rgba(244,245,247,0.7)",
    textMuted: "rgba(244,245,247,0.5)",
    textFaint: "rgba(244,245,247,0.4)",
    accent: "#22C55E",
    accentText: "#22C55E",
    accentSoft: "rgba(34,197,94,0.15)",
    accentBorder: "rgba(34,197,94,0.3)",
    accentShadow:
      "0 18px 40px -14px rgba(34,197,94,0.55), inset 0 1px 0 rgba(255,255,255,0.25)",
    violet: "#7C3AED",
    danger: "#F87171",
    primaryBtnBg: "linear-gradient(180deg, #22C55E, #16A34A)",
    primaryBtnText: "#FFFFFF",
    primaryBtnShadow:
      "0 18px 40px -14px rgba(34,197,94,0.55), inset 0 1px 0 rgba(255,255,255,0.25)",
    secondaryBtnBg: "rgba(255,255,255,0.04)",
    secondaryBtnText: "rgba(244,245,247,0.8)",
    secondaryBtnBorder: "rgba(255,255,255,0.06)",
    ctaBtnBg: "linear-gradient(180deg, #fff, #E5E7EB)",
    ctaBtnText: "#0B0C0E",
    badgeUpBg: "rgba(34,197,94,0.15)",
    badgeUpText: "#22C55E",
    badgeUpBorder: "rgba(34,197,94,0.3)",
    chartGrid: "rgba(255,255,255,0.05)",
    chartStroke: "#22C55E",
    chartGradFrom: "rgba(34,197,94,0.5)",
    chartGradTo: "rgba(34,197,94,0)",
    heroCardBg:
      "linear-gradient(135deg, rgba(34,197,94,0.10), rgba(124,58,237,0.06) 60%, rgba(255,255,255,0.02))",
    sidebarItemActiveBg:
      "linear-gradient(180deg, rgba(34,197,94,0.12), rgba(34,197,94,0.04))",
    sidebarItemActiveBorder: "rgba(34,197,94,0.25)",
    sidebarItemActiveShadow:
      "0 0 0 1px rgba(34,197,94,0.4), 0 10px 28px -10px rgba(34,197,94,0.35)",
    sidebarBadgeBg: "rgba(255,255,255,0.05)",
    sidebarBadgeText: "rgba(244,245,247,0.7)",
    logoBgGradient:
      "linear-gradient(135deg, #22C55E 0%, #16A34A 60%, #14532D 100%)",
    logoShadow:
      "0 12px 32px -8px rgba(34,197,94,0.55), inset 0 1px 0 rgba(255,255,255,0.3)",
    notifDot: "#22C55E",
    notifDotShadow: "0 0 12px rgba(34,197,94,0.8)",
    activityGreen: "#22C55E",
    activityViolet: "#7C3AED",
    activityRed: "#F87171",
    diffEasyBg: "rgba(34,197,94,0.12)",
    diffEasyText: "#22C55E",
    diffEasyBorder: "rgba(34,197,94,0.25)",
    diffMedBg: "rgba(245,158,11,0.12)",
    diffMedText: "#F59E0B",
    diffMedBorder: "rgba(245,158,11,0.25)",
    diffHardBg: "rgba(248,113,113,0.12)",
    diffHardText: "#F87171",
    diffHardBorder: "rgba(248,113,113,0.25)",
    progressTrack: "rgba(255,255,255,0.05)",
    progressFill: "linear-gradient(90deg, #22C55E, #86EFAC)",
    cardShadow: "none",
    divider:
      "linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)",
    loginHaloBg:
      "radial-gradient(900px 500px at 80% 0%, rgba(124,58,237,0.18), transparent 60%), radial-gradient(700px 500px at 0% 100%, rgba(34,197,94,0.12), transparent 60%), #0B0C0E",
    loginCardBg:
      "linear-gradient(180deg, rgba(255,255,255,0.045), rgba(255,255,255,0.015))",
    loginCardBorder: "rgba(255,255,255,0.06)",
  },
  light: {
    name: "light",
    logo: "/brand/quantum-logo.png",
    // Balanced light-mode background with clean alabaster base and theme-aligned emerald accents.
    pageBg: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='28' height='28'><circle cx='1' cy='1' r='0.8' fill='%230b0c0e' fill-opacity='0.04'/></svg>"), radial-gradient(1100px 700px at 5% 15%, rgba(10, 110, 69, 0.07), transparent 65%), radial-gradient(900px 600px at 95% 85%, rgba(10, 110, 69, 0.03), transparent 60%), linear-gradient(180deg, #faf9f6 0%, #f3f1e8 100%)`,
    surfaceBg: "rgba(255,255,255,0.85)",
    sidebarBg: "rgba(252,251,247,0.85)",
    headerBg: "rgba(248,246,240,0.78)",
    border: "rgba(11,12,14,0.07)",
    borderStrong: "rgba(11,12,14,0.12)",
    cardBg:
      "linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.78) 100%)",
    cardTightBg:
      "linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.72) 100%)",
    inputBg: "rgba(255,255,255,0.85)",
    inputBorder: "rgba(11,12,14,0.10)",
    textPrimary: "#0B0C0E",
    textSecondary: "#374151",
    textMuted: "#6B7280",
    textFaint: "#9CA3AF",
    accent: "#0E8554",
    accentText: "#0E8554",
    accentSoft: "rgba(14,133,84,0.10)",
    accentBorder: "rgba(14,133,84,0.30)",
    accentShadow:
      "0 14px 32px -12px rgba(14,133,84,0.45), inset 0 1px 0 rgba(255,255,255,0.4)",
    violet: "#6D28D9",
    danger: "#DC2626",
    primaryBtnBg: "linear-gradient(180deg, #0E8554, #0A6E45)",
    primaryBtnText: "#FFFFFF",
    primaryBtnShadow:
      "0 14px 32px -10px rgba(14,133,84,0.45), inset 0 1px 0 rgba(255,255,255,0.35)",
    secondaryBtnBg: "rgba(255,255,255,0.85)",
    secondaryBtnText: "#374151",
    secondaryBtnBorder: "rgba(11,12,14,0.10)",
    ctaBtnBg: "linear-gradient(180deg, #1F2937, #0B0C0E)",
    ctaBtnText: "#FFFFFF",
    badgeUpBg: "rgba(14,133,84,0.10)",
    badgeUpText: "#0E8554",
    badgeUpBorder: "rgba(14,133,84,0.25)",
    chartGrid: "rgba(11,12,14,0.05)",
    chartStroke: "#0E8554",
    chartGradFrom: "rgba(14,133,84,0.25)",
    chartGradTo: "rgba(14,133,84,0)",
    heroCardBg:
      "linear-gradient(135deg, rgba(14,133,84,0.10) 0%, rgba(124,58,237,0.06) 55%, rgba(255,255,255,0.7) 100%), #FFFFFF",
    sidebarItemActiveBg:
      "linear-gradient(180deg, rgba(14,133,84,0.10), rgba(14,133,84,0.02))",
    sidebarItemActiveBorder: "rgba(14,133,84,0.28)",
    sidebarItemActiveShadow:
      "0 1px 0 rgba(11,12,14,0.02), 0 8px 22px -10px rgba(14,133,84,0.32)",
    sidebarBadgeBg: "rgba(11,12,14,0.04)",
    sidebarBadgeText: "#525252",
    logoBgGradient:
      "linear-gradient(135deg, #0E8554 0%, #0A6E45 60%, #064E3B 100%)",
    logoShadow:
      "0 12px 32px -10px rgba(14,133,84,0.45), inset 0 1px 0 rgba(255,255,255,0.4)",
    notifDot: "#0E8554",
    notifDotShadow: "0 0 0 rgba(0,0,0,0)",
    activityGreen: "#0E8554",
    activityViolet: "#6D28D9",
    activityRed: "#DC2626",
    diffEasyBg: "rgba(14,133,84,0.10)",
    diffEasyText: "#0E8554",
    diffEasyBorder: "rgba(14,133,84,0.25)",
    diffMedBg: "#FFFBEB",
    diffMedText: "#B45309",
    diffMedBorder: "rgba(180,83,9,0.25)",
    diffHardBg: "#FEF2F2",
    diffHardText: "#B91C1C",
    diffHardBorder: "rgba(185,28,28,0.25)",
    progressTrack: "rgba(11,12,14,0.06)",
    progressFill: "linear-gradient(90deg, #0E8554, #34D399)",
    cardShadow:
      "0 1px 0 rgba(11,12,14,0.02), 0 10px 28px -18px rgba(11,12,14,0.12)",
    divider:
      "linear-gradient(90deg, transparent, rgba(11,12,14,0.10), transparent)",
    loginHaloBg: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='28' height='28'><circle cx='1' cy='1' r='0.8' fill='%230b0c0e' fill-opacity='0.04'/></svg>"), radial-gradient(1100px 700px at 5% 15%, rgba(10, 110, 69, 0.07), transparent 65%), radial-gradient(900px 600px at 95% 85%, rgba(10, 110, 69, 0.03), transparent 60%), linear-gradient(180deg, #faf9f6 0%, #f3f1e8 100%)`,
    loginCardBg: "rgba(255,255,255,0.92)",
    loginCardBorder: "rgba(11,12,14,0.08)",
  },
};

/* ─────────────────────────────────────────────────────────────────────────
 * Route component
 * ──────────────────────────────────────────────────────────────────────── */
export default function ThemePreview() {
  const location = useLocation();
  const navigate = useNavigate();

  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const variant = (params.get("variant") || "dark").toLowerCase();
  const section = params.get("section") || "app";
  const t = TOKENS[variant] || TOKENS.dark;

  const setVariant = (next) => {
    const sp = new URLSearchParams(location.search);
    sp.set("variant", next);
    navigate(`${location.pathname}?${sp.toString()}`, { replace: true });
  };
  const setSection = (next) => {
    const sp = new URLSearchParams(location.search);
    sp.set("section", next);
    navigate(`${location.pathname}?${sp.toString()}`, { replace: true });
  };

  useEffect(() => {
    const handler = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable)
        return;
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        const idx = VARIANTS.findIndex((v) => v.key === variant);
        const next =
          e.key === "ArrowRight"
            ? VARIANTS[(idx + 1) % VARIANTS.length]
            : VARIANTS[(idx - 1 + VARIANTS.length) % VARIANTS.length];
        setVariant(next.key);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, location.search]);

  return (
    <>
      {section === "login" ? <LoginView t={t} /> : <AppView t={t} />}
      <PrototypeSwitcher
        variant={variant}
        section={section}
        onVariant={setVariant}
        onSection={setSection}
      />
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 * Floating switcher
 * ──────────────────────────────────────────────────────────────────────── */
function PrototypeSwitcher({ variant, section, onVariant, onSection }) {
  if (
    typeof process !== "undefined" &&
    process.env?.NODE_ENV === "production"
  ) {
    return null;
  }
  return (
    <div
      style={{ fontFamily: "ui-sans-serif, system-ui, sans-serif" }}
      className="fixed bottom-5 left-1/2 -translate-x-1/2 z-9999 flex items-stretch gap-1 rounded-full bg-white text-slate-900 shadow-[0_18px_60px_rgba(0,0,0,0.55)] ring-1 ring-black/10 p-1"
    >
      <div className="flex items-center pl-3 pr-2 text-[11px] font-bold tracking-widest text-slate-400">
        PROTO
      </div>
      <div className="flex items-center p-0.5 rounded-full bg-slate-100">
        {VARIANTS.map((v) => (
          <button
            key={v.key}
            onClick={() => onVariant(v.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
              variant === v.key
                ? "bg-white shadow-sm text-slate-900"
                : "text-slate-500 hover:text-slate-900"
            }`}
          >
            {v.key === "dark" ? (
              <Moon className="h-3.5 w-3.5" />
            ) : (
              <Sun className="h-3.5 w-3.5" />
            )}
            {v.label.split("·")[1]?.trim() || v.label}
          </button>
        ))}
      </div>
      <div className="w-px bg-slate-200 my-1.5 mx-1" />
      <div className="flex items-center p-0.5 rounded-full bg-slate-100">
        {SECTIONS.map((s) => (
          <button
            key={s.key}
            onClick={() => onSection(s.key)}
            className={`px-3 py-1.5 text-xs rounded-full transition-colors font-medium ${
              section === s.key
                ? "bg-slate-900 text-white"
                : "text-slate-500 hover:text-slate-900"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 * Theme-aware <style> block — Tailwind v4 vars + per-instance helpers
 * ──────────────────────────────────────────────────────────────────────── */
function ThemeStyle({ t }) {
  return (
    <style>{`
      .qv-serif {
        font-family: "Cormorant Garamond", "Playfair Display", ui-serif, Georgia, serif;
        font-feature-settings: "ss01";
        letter-spacing: -0.01em;
      }
      .qv-mono {
        font-family: "JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace;
        font-feature-settings: "tnum";
      }
      .qv-card {
        background: ${t.cardBg};
        border: 1px solid ${t.border};
        border-radius: 18px;
        ${t.name === "dark" ? "backdrop-filter: blur(10px);" : ""}
        box-shadow: ${t.cardShadow};
      }
      .qv-card-tight {
        background: ${t.cardTightBg};
        border: 1px solid ${t.border};
        border-radius: 14px;
        box-shadow: ${t.cardShadow};
      }
      .qv-noise { position: relative; }
      .qv-noise::after {
        content: "";
        position: absolute; inset: 0; pointer-events: none; border-radius: inherit;
        background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.4 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>");
        opacity: ${t.name === "dark" ? "0.05" : "0.025"};
        mix-blend-mode: ${t.name === "dark" ? "overlay" : "multiply"};
      }
      .qv-divider { background: ${t.divider}; height: 1px; }
      @keyframes pulseDot { 0%,100% { opacity: 0.45; } 50% { opacity: 1; } }
      .qv-pulse { animation: pulseDot 2.4s ease-in-out infinite; }
    `}</style>
  );
}

/* ═════════════════════════════════════════════════════════════════════════
 * APP VIEW — mirrors AdminLayout: w-60 sidebar (left) + header + main
 * ════════════════════════════════════════════════════════════════════════ */
function AppView({ t }) {
  return (
    <div
      style={{
        background: t.pageBg,
        color: t.textPrimary,
        fontFamily:
          "'Inter', ui-sans-serif, system-ui, -apple-system, sans-serif",
        fontFeatureSettings: '"ss01","cv11","tnum"',
        minHeight: "100vh",
      }}
    >
      <ThemeStyle t={t} />
      <Sidebar t={t} />
      <div className="lg:ml-60 flex flex-col min-h-screen">
        <Header t={t} />
        <Dashboard t={t} />
      </div>
    </div>
  );
}

function Sidebar({ t }) {
  const items = [
    { icon: LayoutDashboard, label: "Dashboard", active: true },
    { icon: Building2, label: "Register Organizations" },
    { icon: UserPlus, label: "Register Admins" },
    { icon: Users, label: "User Management" },
    { icon: Server, label: "Boxes", badge: "312" },
    { icon: Shield, label: "Scenarios", badge: "146" },
    { icon: Activity, label: "Analytics" },
  ];
  return (
    <aside
      style={{
        background: t.sidebarBg,
        borderRight: `1px solid ${t.border}`,
        backdropFilter: "blur(20px)",
      }}
      className="fixed top-0 left-0 h-screen w-60 z-30 hidden lg:flex flex-col"
    >
      {/* Logo — matches real Navbar/Sidebar */}
      <div
        className="flex items-center gap-3 px-5 py-4 min-h-19"
        style={{ borderBottom: `1px solid ${t.border}` }}
      >
        <img
          src={t.logo}
          alt="Quantum Vanguard"
          className="h-12 w-12 object-contain shrink-0"
        />
        <span
          className="font-bold text-xl tracking-wide leading-none"
          style={{ color: t.accent }}
        >
          Quantum Vanguard
        </span>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        <div
          className="px-3 text-[10.5px] tracking-[0.22em] uppercase mb-2"
          style={{ color: t.textFaint }}
        >
          Workspace
        </div>
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <button
              key={it.label}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13.5px] font-medium transition-all whitespace-nowrap"
              style={{
                color: it.active ? t.textPrimary : t.textSecondary,
                background: it.active
                  ? t.sidebarItemActiveBg
                  : "transparent",
                border: it.active
                  ? `1px solid ${t.sidebarItemActiveBorder}`
                  : "1px solid transparent",
                boxShadow: it.active ? t.sidebarItemActiveShadow : "none",
              }}
            >
              <Icon
                className="h-4.5 w-4.5 shrink-0"
                style={{ color: it.active ? t.accent : t.textMuted }}
              />
              <span className="flex-1 text-left truncate">{it.label}</span>
              {it.badge && (
                <span
                  className="text-[10.5px] px-1.5 py-0.5 rounded-md qv-mono"
                  style={{
                    background: t.sidebarBadgeBg,
                    color: t.sidebarBadgeText,
                  }}
                >
                  {it.badge}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Logout footer — matches real PlatformAdminSidebar */}
      <div className="px-3 py-4" style={{ borderTop: `1px solid ${t.border}` }}>
        <button
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13.5px] font-medium transition-colors"
          style={{ color: t.danger }}
        >
          <LogOut className="h-4.25 w-4.25" />
          Logout
        </button>
      </div>
    </aside>
  );
}

function Header({ t }) {
  return (
    <header
      style={{
        background: t.headerBg,
        backdropFilter: "blur(18px) saturate(140%)",
      }}
      className="sticky top-0 z-20 flex items-center justify-between px-3 sm:px-6 min-h-16 sm:min-h-19"
    >
      {/* Hamburger — mobile */}
      <button
        className="lg:hidden p-2 -ml-2 mr-1 rounded-lg"
        style={{ color: t.textMuted }}
        aria-label="Open navigation"
      >
        <Menu className="h-5 w-5" />
      </button>

      <div className="flex-1" />

      <div className="flex items-center gap-2 sm:gap-3">
        {/* Search — desktop */}
        <div
          className="hidden sm:flex items-center gap-2 px-3 h-10 rounded-xl"
          style={{
            background: t.inputBg,
            border: `1px solid ${t.inputBorder}`,
          }}
        >
          <Search className="h-4 w-4" style={{ color: t.textMuted }} />
          <input
            placeholder="Search resources…"
            className="bg-transparent outline-none w-44 text-[13.5px]"
            style={{ color: t.textPrimary }}
          />
          <span
            className="text-[10.5px] px-2 py-0.5 rounded-md qv-mono"
            style={{
              background: t.sidebarBadgeBg,
              color: t.sidebarBadgeText,
            }}
          >
            ⌘ K
          </span>
        </div>

        {/* Language */}
        <button
          className="h-10 px-3 rounded-xl text-[12.5px] font-medium flex items-center gap-1.5"
          style={{
            background: t.secondaryBtnBg,
            border: `1px solid ${t.secondaryBtnBorder}`,
            color: t.secondaryBtnText,
          }}
        >
          <Globe className="h-4 w-4" />
          EN
        </button>

        {/* Bell */}
        <button
          className="relative h-10 w-10 rounded-xl grid place-items-center"
          style={{
            background: t.secondaryBtnBg,
            border: `1px solid ${t.secondaryBtnBorder}`,
            color: t.secondaryBtnText,
          }}
        >
          <Bell className="h-4 w-4" />
          <span
            className="absolute top-2 right-2 h-2 w-2 rounded-full"
            style={{
              background: t.notifDot,
              boxShadow: t.notifDotShadow,
            }}
          />
        </button>

        {/* User pill */}
        <div
          className="flex items-center gap-3 pl-4 pr-1 h-11 rounded-2xl"
          style={{
            background: t.secondaryBtnBg,
            border: `1px solid ${t.secondaryBtnBorder}`,
          }}
        >
          <div className="text-right leading-tight hidden md:block">
            <div
              className="text-[12.5px] font-medium"
              style={{ color: t.textPrimary }}
            >
              A. Khattab
            </div>
            <div className="text-[10.5px]" style={{ color: t.textMuted }}>
              Platform Admin
            </div>
          </div>
          <div
            className="h-9 w-9 rounded-full grid place-items-center text-[12px] font-semibold text-white"
            style={{
              background: t.logoBgGradient,
              boxShadow: t.logoShadow,
            }}
          >
            AK
          </div>
        </div>
      </div>
    </header>
  );
}

function Dashboard({ t }) {
  return (
    <main className="p-6 lg:p-8 space-y-6">
      {/* Greeting */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div
            className="text-[11px] tracking-[0.22em] uppercase"
            style={{ color: t.textMuted }}
          >
            Friday · May 18, 2026
          </div>
          <h1
            className="qv-serif mt-2 text-[40px] md:text-[44px] leading-none font-medium"
            style={{ color: t.textPrimary }}
          >
            Good afternoon, <span style={{ color: t.accent }}>Abrar</span>.
          </h1>
          <p
            className="mt-2 text-[14px]"
            style={{ color: t.textSecondary }}
          >
            Your range is healthy. 7 incidents resolved, 312 boxes active, MTTR down 3.4%.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            className="h-10 px-4 rounded-full text-[12.5px] font-medium"
            style={{
              background: t.secondaryBtnBg,
              border: `1px solid ${t.secondaryBtnBorder}`,
              color: t.secondaryBtnText,
            }}
          >
            Export report
          </button>
          <button
            className="h-10 px-5 rounded-full text-[12.5px] font-semibold flex items-center gap-2"
            style={{
              background: t.ctaBtnBg,
              color: t.ctaBtnText,
              boxShadow:
                t.name === "dark"
                  ? "0 12px 32px -12px rgba(255,255,255,0.35)"
                  : "0 12px 32px -12px rgba(11,12,14,0.45)",
            }}
          >
            <Sparkles className="h-4 w-4" />
            Launch scenario
          </button>
        </div>
      </div>

      {/* Hero */}
      <section
        className="qv-card qv-noise p-7 flex flex-col md:flex-row items-stretch md:items-center gap-7"
        style={{ background: t.heroCardBg }}
      >
        <div className="flex-1">
          <div
            className="text-[11px] tracking-[0.22em] uppercase"
            style={{ color: t.textMuted }}
          >
            Active sessions · last 24h
          </div>
          <div className="mt-3 flex items-baseline gap-4">
            <span
              className="qv-serif text-[60px] leading-none font-medium tracking-tight"
              style={{ color: t.textPrimary }}
            >
              1,284
            </span>
            <span
              className="text-[13.5px] font-semibold flex items-center gap-1 px-2.5 py-1 rounded-full"
              style={{
                background: t.badgeUpBg,
                color: t.badgeUpText,
                border: `1px solid ${t.badgeUpBorder}`,
              }}
            >
              <ArrowUpRight className="h-3.5 w-3.5" />
              +4.2%
            </span>
          </div>
          <div
            className="mt-4 text-[12.5px]"
            style={{ color: t.textSecondary }}
          >
            vs. 1,232 yesterday · 54 new since 09:00
          </div>
        </div>

        <div className="flex-1 h-30">
          <svg viewBox="0 0 600 120" className="w-full h-full">
            <defs>
              <linearGradient id={`heroGrad-${t.name}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={t.chartGradFrom} stopOpacity="1" />
                <stop offset="100%" stopColor={t.chartGradTo} stopOpacity="1" />
              </linearGradient>
            </defs>
            <path
              d="M0,90 C40,70 80,82 120,68 C160,56 200,72 240,52 C280,38 320,48 360,30 C400,18 440,32 480,24 C520,18 560,28 600,16 L600,120 L0,120 Z"
              fill={`url(#heroGrad-${t.name})`}
            />
            <path
              d="M0,90 C40,70 80,82 120,68 C160,56 200,72 240,52 C280,38 320,48 360,30 C400,18 440,32 480,24 C520,18 560,28 600,16"
              fill="none"
              stroke={t.chartStroke}
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </div>
      </section>

      {/* KPI tiles */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          {
            label: "Running scenarios",
            value: "146",
            delta: "+2",
            tint: t.accent,
            icon: Shield,
          },
          {
            label: "Boxes online",
            value: "312",
            delta: "+1.8%",
            tint: t.violet,
            icon: Server,
          },
          {
            label: "Avg. MTTR",
            value: "11m 24s",
            delta: "-3.4%",
            tint: "#F59E0B",
            icon: Activity,
          },
        ].map((k) => {
          const Icon = k.icon;
          return (
            <div key={k.label} className="qv-card qv-noise p-5">
              <div className="flex items-center justify-between">
                <div
                  className="h-10 w-10 rounded-xl grid place-items-center"
                  style={{
                    background: `${k.tint}${t.name === "dark" ? "20" : "14"}`,
                    border: `1px solid ${k.tint}${t.name === "dark" ? "40" : "30"}`,
                  }}
                >
                  <Icon className="h-4.5 w-4.5" style={{ color: k.tint }} />
                </div>
                <span
                  className="text-[12px] font-semibold flex items-center gap-0.5 px-2 py-0.5 rounded-full"
                  style={{
                    color: t.badgeUpText,
                    background: t.badgeUpBg,
                  }}
                >
                  <ArrowUpRight className="h-3.5 w-3.5" />
                  {k.delta}
                </span>
              </div>
              <div
                className="mt-5 text-[11px] tracking-[0.22em] uppercase"
                style={{ color: t.textMuted }}
              >
                {k.label}
              </div>
              <div
                className="qv-serif mt-1.5 text-[36px] font-medium tracking-tight"
                style={{ color: t.textPrimary }}
              >
                {k.value}
              </div>
            </div>
          );
        })}
      </div>

      {/* Scenarios + activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="qv-card qv-noise p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div
                className="text-[15px] font-semibold"
                style={{ color: t.textPrimary }}
              >
                Top scenarios
              </div>
              <div
                className="text-[12px] mt-0.5"
                style={{ color: t.textMuted }}
              >
                Sorted by sessions, last 24 hours
              </div>
            </div>
            <button
              className="text-[12px] flex items-center gap-1"
              style={{ color: t.textSecondary }}
            >
              View all <ArrowUpRight className="h-3.5 w-3.5" />
            </button>
          </div>
          <ul className="space-y-2.5">
            {[
              ["Active Directory · Kerberoasting", "HARD", 412, "78%"],
              ["Web Pivot · IDOR Chain", "MED", 388, "82%"],
              ["Cloud · S3 Privilege Escalation", "HARD", 296, "71%"],
              ["Linux PrivEsc · Pwnkit", "MED", 274, "88%"],
              ["Phishing · OAuth Consent", "EASY", 221, "94%"],
            ].map((r, i) => {
              const diffStyle =
                r[1] === "HARD"
                  ? { bg: t.diffHardBg, text: t.diffHardText, border: t.diffHardBorder }
                  : r[1] === "MED"
                    ? { bg: t.diffMedBg, text: t.diffMedText, border: t.diffMedBorder }
                    : { bg: t.diffEasyBg, text: t.diffEasyText, border: t.diffEasyBorder };
              return (
                <li
                  key={i}
                  className="qv-card-tight p-3.5 flex items-center gap-4"
                >
                  <div
                    className="h-10 w-10 rounded-xl grid place-items-center text-[12px] font-semibold shrink-0"
                    style={{
                      background: diffStyle.bg,
                      color: diffStyle.text,
                      border: `1px solid ${diffStyle.border}`,
                    }}
                  >
                    {r[1]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div
                      className="text-[13.5px] font-medium truncate"
                      style={{ color: t.textPrimary }}
                    >
                      {r[0]}
                    </div>
                    <div className="mt-1 flex items-center gap-3">
                      <div
                        className="h-1 flex-1 rounded-full overflow-hidden"
                        style={{ background: t.progressTrack }}
                      >
                        <div
                          className="h-full"
                          style={{
                            width: r[3],
                            background: t.progressFill,
                          }}
                        />
                      </div>
                      <span
                        className="text-[11px] qv-mono"
                        style={{ color: t.textSecondary }}
                      >
                        {r[3]}
                      </span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div
                      className="qv-serif text-[22px] leading-none"
                      style={{ color: t.textPrimary }}
                    >
                      {r[2]}
                    </div>
                    <div
                      className="text-[10.5px] tracking-[0.18em] uppercase"
                      style={{ color: t.textMuted }}
                    >
                      sessions
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="qv-card qv-noise p-6">
          <div className="flex items-center justify-between mb-5">
            <div
              className="text-[15px] font-semibold"
              style={{ color: t.textPrimary }}
            >
              Live activity
            </div>
            <span
              className="text-[10.5px] flex items-center gap-1.5 tracking-[0.18em] uppercase"
              style={{ color: t.accentText }}
            >
              <span
                className="h-1.5 w-1.5 rounded-full qv-pulse"
                style={{
                  background: t.accent,
                  boxShadow:
                    t.name === "dark" ? `0 0 8px ${t.accent}` : "none",
                }}
              />
              Live
            </span>
          </div>
          <ul className="space-y-4">
            {[
              ["BOX-1142 spun up", "Just now", t.activityGreen],
              ["a.khattab signed in", "2 min ago", t.activityViolet],
              ["scen.web-pivot started", "3 min ago", t.activityGreen],
              ["Incident #IN-029 resolved", "5 min ago", t.activityGreen],
              ["BOX-1138 failed to provision", "8 min ago", t.activityRed],
              ["scen.privesc-lin submitted", "11 min ago", t.activityGreen],
              ["h.akram registered", "14 min ago", t.activityViolet],
            ].map((row, i) => (
              <li key={i} className="flex items-start gap-3">
                <span
                  className="mt-1.5 h-2 w-2 rounded-full shrink-0"
                  style={{
                    background: row[2],
                    boxShadow:
                      t.name === "dark" ? `0 0 10px ${row[2]}` : "none",
                  }}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px]" style={{ color: t.textPrimary }}>
                    {row[0]}
                  </div>
                  <div className="text-[11px]" style={{ color: t.textMuted }}>
                    {row[1]}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </main>
  );
}

/* ═════════════════════════════════════════════════════════════════════════
 * LOGIN VIEW — cinematic two-column with real logo
 * ════════════════════════════════════════════════════════════════════════ */
function LoginView({ t }) {
  const [show, setShow] = useState(false);
  return (
    <div
      style={{
        background: t.loginHaloBg,
        color: t.textPrimary,
        fontFamily:
          "'Inter', ui-sans-serif, system-ui, -apple-system, sans-serif",
        fontFeatureSettings: '"ss01","cv11","tnum"',
      }}
      className="min-h-screen grid grid-cols-1 lg:grid-cols-2"
    >
      <ThemeStyle t={t} />

      {/* Brand pane */}
      <div className="hidden lg:flex flex-col justify-between p-14">
        <div className="flex items-center gap-3">
          <img
            src={t.logo}
            alt="Quantum Vanguard"
            className="h-12 w-12 object-contain"
          />
          <div className="leading-tight">
            <div
              className="text-[17px] font-semibold tracking-tight"
              style={{ color: t.accent }}
            >
              Quantum Vanguard
            </div>
            <div className="text-[11.5px]" style={{ color: t.textMuted }}>
              Enterprise Cyber Range
            </div>
          </div>
        </div>

        <div>
          <div
            className="text-[11px] tracking-[0.28em] uppercase mb-4"
            style={{ color: t.accent }}
          >
            ── Welcome back
          </div>
          <h1
            className="qv-serif text-[60px] leading-[0.95] font-medium tracking-tight"
            style={{ color: t.textPrimary }}
          >
            Train like
            <br />
            <span style={{ color: t.accent }}>adversaries</span>.
            <br />
            Defend like
            <br />
            architects.
          </h1>
          <p
            className="mt-7 max-w-md text-[14px] leading-relaxed"
            style={{ color: t.textSecondary }}
          >
            The platform behind the world's most rigorous SOC and red-team
            programs. Realistic ranges, instrumented adversary emulation,
            cinematic visibility.
          </p>

          <div className="mt-10 flex items-center gap-6">
            {["ISO 27001", "SOC 2 Type II", "NIST CSF"].map((c) => (
              <div
                key={c}
                className="text-[10.5px] tracking-[0.22em] uppercase"
                style={{ color: t.textMuted }}
              >
                {c}
              </div>
            ))}
          </div>
        </div>

        <div className="text-[11px]" style={{ color: t.textFaint }}>
          © 2026 Quantum Vanguard. All systems nominal.
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-8">
        <div
          className="qv-card qv-noise w-full max-w-md p-9"
          style={{
            background: t.loginCardBg,
            borderColor: t.loginCardBorder,
          }}
        >
          {/* mobile brand */}
          <div className="flex lg:hidden items-center gap-3 mb-6">
            <img
              src={t.logo}
              alt="Quantum Vanguard"
              className="h-10 w-10 object-contain"
            />
            <div className="font-semibold tracking-tight" style={{ color: t.accent }}>
              Quantum Vanguard
            </div>
          </div>

          <div
            className="text-[10.5px] tracking-[0.28em] uppercase"
            style={{ color: t.textMuted }}
          >
            Sign in
          </div>
          <h2
            className="qv-serif mt-2 text-[32px] font-medium tracking-tight"
            style={{ color: t.textPrimary }}
          >
            Enter the range.
          </h2>
          <p className="mt-1 text-[13px]" style={{ color: t.textSecondary }}>
            Use your enterprise identity to continue.
          </p>

          <div className="mt-7 space-y-4">
            <div>
              <label
                className="text-[11px] tracking-[0.2em] uppercase"
                style={{ color: t.textMuted }}
              >
                Email
              </label>
              <div
                className="mt-1.5 h-12 rounded-xl flex items-center px-4"
                style={{
                  background: t.inputBg,
                  border: `1px solid ${t.inputBorder}`,
                }}
              >
                <input
                  defaultValue="a.khattab@qv.enterprise"
                  className="flex-1 bg-transparent outline-none text-[14px]"
                  style={{ color: t.textPrimary }}
                />
              </div>
            </div>
            <div>
              <label
                className="text-[11px] tracking-[0.2em] uppercase"
                style={{ color: t.textMuted }}
              >
                Password
              </label>
              <div
                className="mt-1.5 h-12 rounded-xl flex items-center px-4"
                style={{
                  background: t.inputBg,
                  border: `1px solid ${t.inputBorder}`,
                }}
              >
                <input
                  type={show ? "text" : "password"}
                  defaultValue="••••••••••••"
                  className="flex-1 bg-transparent outline-none text-[14px]"
                  style={{ color: t.textPrimary }}
                />
                <button
                  onClick={() => setShow((s) => !s)}
                  style={{ color: t.textMuted }}
                >
                  {show ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between text-[12.5px]">
              <label
                className="flex items-center gap-2"
                style={{ color: t.textSecondary }}
              >
                <span
                  className="h-4 w-4 rounded-md grid place-items-center"
                  style={{
                    background: t.accentSoft,
                    border: `1px solid ${t.accentBorder}`,
                  }}
                >
                  <span
                    className="h-1.5 w-1.5 rounded-sm"
                    style={{ background: t.accent }}
                  />
                </span>
                Keep me signed in
              </label>
              <a
                style={{ color: t.accent, fontWeight: 500 }}
                className="cursor-pointer"
              >
                Forgot?
              </a>
            </div>

            <button
              className="w-full h-12 rounded-xl font-semibold text-[14px] flex items-center justify-center gap-2 text-white"
              style={{
                background: t.primaryBtnBg,
                boxShadow: t.primaryBtnShadow,
              }}
            >
              Continue
              <ArrowUpRight className="h-4 w-4" />
            </button>

            <div className="flex items-center gap-3 my-2">
              <div className="qv-divider flex-1" />
              <div
                className="text-[10.5px] tracking-[0.22em] uppercase"
                style={{ color: t.textFaint }}
              >
                or
              </div>
              <div className="qv-divider flex-1" />
            </div>

            <div className="grid grid-cols-2 gap-2.5">
              {["Okta SSO", "Microsoft Entra"].map((s) => (
                <button
                  key={s}
                  className="h-11 rounded-xl text-[12.5px] font-medium"
                  style={{
                    background: t.secondaryBtnBg,
                    border: `1px solid ${t.secondaryBtnBorder}`,
                    color: t.secondaryBtnText,
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
